<template>
  <Transition
    enter-active-class="transition ease-out duration-500"
    enter-from-class="opacity-0 scale-95"
    enter-to-class="opacity-100 scale-100"
    leave-active-class="transition ease-in duration-500"
    leave-from-class="opacity-100 scale-100"
    leave-to-class="opacity-0 scale-95"
    @after-leave="afterLeave"
  >
    <div
      v-if="internalVisible"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click.self="close"
    >
      <div
        class="bg-white rounded-lg p-6"
        :style="{
          width: modalWidth,
          height: modalHeight,
          maxWidth: modalMaxWidth,
          maxHeight: modalMaxHeight
        }"
      >
        <slot :close="close"></slot>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, watch, provide } from 'vue';
import {useToast} from 'vue-toastification';
import {triggerToastEventName} from "@/shared/config/constants.ts";
import {ToastLevel} from "@/shared/config/levels.ts";

const toast = useToast();

// Function to trigger the toast with level and message
const triggerToast = (level: ToastLevel, message: string) => {
  switch (level) {
    case ToastLevel.Success:
      toast.success(message);
      break;
    case ToastLevel.Error:
      toast.error(message);
      break;
    case ToastLevel.Warning:
      toast.warning(message);
      break;
    case ToastLevel.Info:
      toast.info(message);
      break;
    default:
      toast.info(message);
  }
};

// Provide the triggerToast function to the entire app
provide(triggerToastEventName, triggerToast);

const props = defineProps({
  open: {
    type: Boolean,
    required: true,
  },
  modalWidth: {
    type: String,
    default: '500px'
  },
  modalHeight: {
    type: String,
    default: 'auto'
  },
  modalMaxWidth: {
    type: String,
    default: '90%'
  },
  modalMaxHeight: {
    type: String,
    default: '90%'
  }
});

// Estado interno para la visibilidad, que se actualiza según la prop "open"
const internalVisible = ref(props.open);

// Emitir "close" cuando se complete la animación de salida
const emit = defineEmits(['close']);

// Observa cambios en la prop "open" para actualizar el estado interno
watch(
  () => props.open,
  (newVal) => {
    internalVisible.value = newVal;
  }
);

// Función para cerrar el modal (por ejemplo, al hacer clic en el fondo)
const close = () => {
  internalVisible.value = false;
};

// Después de la animación de salida, se emite el evento "close"
const afterLeave = () => {
  emit('close');
};
</script>
